import React, { useEffect, useState, memo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Map as MapIcon, Sparkles, Shield, Crown, BookOpen, ScrollText, Landmark, Users, Trophy, ChevronRight, X, Star, Bus, Ticket, ArrowRight, Flame, History, Gem, GraduationCap, HeartHandshake } from 'lucide-react';
import { Link } from 'react-router-dom';
// --- MOCK DATA ---
const ZONES = [{
  id: 'lahore-court',
  name: 'Lahore Court',
  description: 'Restore the portraits, seals, court scenes, and authority of the Lahore Darbar.',
  accepted: 'Portraits, seals, court scenes',
  progress: 42,
  cardsContributed: 128,
  requiredCards: 300,
  icon: Landmark,
  color: 'from-[#D4AF37]/20 to-transparent',
  borderColor: 'border-[#D4AF37]/30'
}, {
  id: 'sacred-punjab',
  name: 'Sacred Punjab',
  description: 'Restore the sacred objects, sites, and symbols of reverence before rule.',
  accepted: 'Harmandir Sahib, Nishan Sahib, Hukamnama',
  progress: 68,
  cardsContributed: 85,
  requiredCards: 125,
  icon: Flame,
  color: 'from-amber-500/20 to-transparent',
  borderColor: 'border-amber-500/30'
}, {
  id: 'frontier-defenders',
  name: 'Frontier Defenders',
  description: 'Restore the forts, standards, shields, and defenders of the empire’s edge.',
  accepted: 'Hari Singh Nalwa, Jamrud Fort, shields',
  progress: 24,
  cardsContributed: 45,
  requiredCards: 180,
  icon: Shield,
  color: 'from-slate-400/20 to-transparent',
  borderColor: 'border-slate-400/30'
}, {
  id: 'treasury-vault',
  name: 'Treasury Vault',
  description: 'Restore the jewels, regalia, coins, and objects of sovereign prestige.',
  accepted: 'Koh-i-Noor, Gold Mohur, Sarpech',
  progress: 89,
  cardsContributed: 210,
  requiredCards: 235,
  icon: Gem,
  color: 'from-emerald-500/20 to-transparent',
  borderColor: 'border-emerald-500/30'
}, {
  id: 'royal-women',
  name: 'Royal Women & Chamber Culture',
  description: 'Restore the inner court of lineage, counsel, music, memory, and refinement.',
  accepted: 'Maharani Jind Kaur, Moran Sarkar',
  progress: 15,
  cardsContributed: 22,
  requiredCards: 150,
  icon: Crown,
  color: 'from-rose-500/20 to-transparent',
  borderColor: 'border-rose-500/30'
}, {
  id: 'fracture-memory',
  name: 'Fracture & Memory',
  description: 'Restore the letters, broken seals, lost treasures, and emotional memory.',
  accepted: 'Broken Seal, Letter from Exile',
  progress: 5,
  cardsContributed: 8,
  requiredCards: 100,
  icon: History,
  color: 'from-indigo-500/20 to-transparent',
  borderColor: 'border-indigo-500/30'
}];
const USER_CARDS = [{
  id: 1,
  name: 'Maharaja Ranjit Singh',
  rarity: "Founders' Edition",
  value: 50,
  img: 'https://cdn.magicpatterns.com/uploads/8Kp4p7xwsPGGj2bxMr4kRM/Ranjit_Singh.png'
}, {
  id: 2,
  name: 'Hari Singh Nalwa',
  rarity: 'Epic',
  value: 25,
  img: 'https://cdn.magicpatterns.com/uploads/ekyaLxoHxw9b6NSM5wgnik/Hari_Singh_Nalwa.png'
}, {
  id: 3,
  name: 'Gold Mohur',
  rarity: 'Rare',
  value: 15,
  img: 'https://cdn.magicpatterns.com/uploads/b9oEVYjaVfcwC8HyTuv84g/image.png'
}];
export function RestorePunjabPage() {
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [isContributeModalOpen, setIsContributeModalOpen] = useState(false);
  const [animatingCard, setAnimatingCard] = useState<number | null>(null);
  const handleContribute = (cardId: number) => {
    setAnimatingCard(cardId);
    setTimeout(() => {
      setAnimatingCard(null);
      setIsContributeModalOpen(false);
      // Show success toast here in real app
    }, 1500);
  };
  return <div className="min-h-screen bg-[#0B0C10] text-[#FDFBF7] font-sans selection:bg-[#D4AF37]/30" data-id="element-6721" data-mp="1ki97hhfb8ooh">
      {/* Navigation Bar (Simplified for standalone page) */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0B0C10]/80 backdrop-blur-md border-b border-white/5" data-id="element-6722" data-mp="3flfctlq6gmzz">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between" data-id="element-6723" data-mp="3kk0c9ixlv2nd">
          <Link to="/" className="font-serif text-xl text-[#D4AF37] tracking-wider" data-id="element-6724" data-mp="35wxfjeh3wy13">
            THE KINGDOMS OF PUNJAB
          </Link>
          <div className="flex items-center gap-6" data-id="element-6725" data-mp="2wvh3l2an8nur">
            <Link to="/treasure" className="text-sm text-white/60 hover:text-white transition-colors" data-id="element-6726" data-mp="2wuo7qwd83ire">
              Lahore Treasury
            </Link>
            <button onClick={() => setIsContributeModalOpen(true)} className="bg-[#D4AF37] text-black px-5 py-2 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors" data-id="element-6727" data-mp="2myl4toajrg3e">
              Contribute a Card
            </button>
          </div>
        </div>
      </nav>

      {/* SECTION 1: Restoration Hero */}
      <section className="relative pt-32 pb-20 min-h-[90vh] flex flex-col items-center justify-center overflow-hidden" data-id="element-6728" data-mp="2amsi8z79moa2">
        {/* Background Map Graphic */}
        <div className="absolute inset-0 z-0 opacity-20 pointer-events-none flex items-center justify-center" data-id="element-6729" data-mp="25psbem2ldhhw">
          <div className="w-[800px] h-[800px] rounded-full border border-[#D4AF37]/20 absolute animate-[spin_120s_linear_infinite]" data-id="element-6730" data-mp="2keya88xip5p9" />
          <div className="w-[600px] h-[600px] rounded-full border border-[#D4AF37]/10 absolute animate-[spin_90s_linear_infinite_reverse]" data-id="element-6731" data-mp="2td9za6iqjvfl" />
          <MapIcon className="w-[400px] h-[400px] text-[#D4AF37] opacity-10" data-id="element-6732" data-mp="2odxahg8b0ead" />
        </div>

        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B0C10]/80 to-[#0B0C10] z-0" data-id="element-6733" data-mp="33aemm9obhwxz" />

        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center" data-id="element-6734" data-mp="2yd6427z9bqw7">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} data-id="element-6735" data-mp="18yq1fprhsy55">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-6 flex items-center justify-center gap-3" data-id="element-6736" data-mp="1xo2vxjea2z8t">
              <Sparkles className="w-4 h-4" data-id="element-6737" data-mp="22me1vj1gcplr" />
              The Punjab Memory Map
              <Sparkles className="w-4 h-4" data-id="element-6738" data-mp="27n1to6xi9ah1" />
            </p>
            <h1 className="font-serif text-5xl sm:text-7xl text-[#FDFBF7] mb-6 leading-tight" data-id="element-6739" data-mp="27kfnobq9j30x">
              Restore the Memory <br className="hidden sm:block" data-id="element-6740" data-mp="2gn6xy87ejsqz" />
              <span className="text-[#D4AF37]" data-id="element-6741" data-mp="2glvvqctlut1z">of Punjab</span>
            </h1>
            <p className="text-lg sm:text-xl text-white/60 font-light max-w-2xl mx-auto mb-12 leading-relaxed" data-id="element-6742" data-mp="2ldnbdsb83edc">
              Collect Lahore Treasury cards and use them to illuminate the lost
              places, people, treasures, and stories of the Sikh Empire.
            </p>

            {/* Progress Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-12" data-id="element-6743" data-mp="30bza04i5ox75">
              {[{
              label: 'Overall Restoration',
              value: '34%'
            }, {
              label: 'Cards Contributed',
              value: '18'
            }, {
              label: 'Zones Restored',
              value: '2 / 6'
            }, {
              label: 'Next Unlock',
              value: 'Treasury Night'
            }].map((stat, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-lg p-4 backdrop-blur-sm" data-id="element-6744" data-mp="12fr26ajr6tlk" data-mpi={i}>
                  <p className="text-white/40 text-xs uppercase tracking-wider mb-1" data-id="element-6745" data-mp="spz7lgbnm0h3">
                    {stat.label}
                  </p>
                  <p className="text-[#D4AF37] font-serif text-2xl" data-id="element-6746" data-mp="1q6egbdk9ifgk">
                    {stat.value}
                  </p>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-6747" data-mp="tph38emfgi79">
              <button onClick={() => setIsContributeModalOpen(true)} className="w-full sm:w-auto bg-[#D4AF37] text-black px-8 py-4 rounded-sm text-sm font-medium tracking-wide hover:bg-[#FDFBF7] transition-all shadow-[0_0_30px_rgba(212,175,55,0.3)]" data-id="element-6748" data-mp="yiaab95dc2mi">
                Start Restoring
              </button>
              <Link to="/treasure" className="w-full sm:w-auto bg-white/5 border border-white/10 text-white px-8 py-4 rounded-sm text-sm font-medium tracking-wide hover:bg-white/10 transition-all" data-id="element-6749" data-mp="1dcomapqimbro">
                Open a Court Packet
              </Link>
              <a href="#schools" className="w-full sm:w-auto text-white/60 hover:text-[#D4AF37] px-6 py-4 text-sm font-medium tracking-wide transition-colors" data-id="element-6750" data-mp="18h96hs8y65sm">
                For Schools →
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SECTION 2: How Restoration Works */}
      <section className="py-24 bg-[#0B0C10] relative border-t border-white/5" data-id="element-6751" data-mp="3gmjmqo6p0nrh">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6752" data-mp="3ulj89mywsmjf">
          <div className="text-center mb-16" data-id="element-6753" data-mp="3pnq2gv1ipujo">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6754" data-mp="3pp16mvyvl4bb">
              How Restoration Works
            </h2>
            <div className="w-16 h-px bg-[#D4AF37] mx-auto" data-id="element-6755" data-mp="31w2k51ibnrt0" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-6" data-id="element-6756" data-mp="2xy4uxyojx5pm">
            {[{
            icon: ScrollText,
            title: 'Open Packets',
            desc: 'Reveal portraits, seals, treasures, and memory fragments.'
          }, {
            icon: BookOpen,
            title: 'Learn the Story',
            desc: 'Each card includes lore, historical notes, and audio.'
          }, {
            icon: MapIcon,
            title: 'Choose a Zone',
            desc: 'Contribute cards to specific historical regions.'
          }, {
            icon: Sparkles,
            title: 'Illuminate Map',
            desc: 'Restore visual detail, story fragments, and progress.'
          }, {
            icon: Ticket,
            title: 'Unlock Live',
            desc: 'Reach milestones to unlock Live Night rewards.'
          }].map((step, i) => <div key={i} className="relative" data-id="element-6757" data-mp="q365vyvethqa" data-mpi={i}>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 h-full hover:border-[#D4AF37]/30 transition-colors group" data-id="element-6758" data-mp="l6xu8vgf86nv">
                  <div className="w-10 h-10 rounded-full bg-[#D4AF37]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform" data-id="element-6759" data-mp="g4qfjy47ichu">
                    <step.icon className="w-5 h-5 text-[#D4AF37]" data-id="element-6760" data-mp="776byiivg442" />
                  </div>
                  <h3 className="font-serif text-lg text-white mb-2" data-id="element-6761" data-mp="2iu9s9nnv2msp">
                    {step.title}
                  </h3>
                  <p className="text-white/50 text-sm leading-relaxed" data-id="element-6762" data-mp="2nr06s6k0pac0">
                    {step.desc}
                  </p>
                </div>
                {i < 4 && <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-white/10" data-id="element-6763" data-mp="2ws6rkrtp97b2" />}
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 3: Restoration Zones */}
      <section className="py-24 bg-[#111218] relative border-t border-white/5" data-id="element-6764" data-mp="244u84qiu5flz">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6765" data-mp="292430lozujh7">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6" data-id="element-6766" data-mp="2du3xhymaok6e">
            <div data-id="element-6767" data-mp="1gezwgca95x3b">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6768" data-mp="1gfj9bel1pvah">
                Restoration Zones
              </h2>
              <p className="text-white/60 max-w-2xl" data-id="element-6769" data-mp="1lci5e2o9scbb">
                Contribute your collectibles to illuminate specific regions of
                the Punjab Memory Map. Each zone requires different types of
                historical artifacts.
              </p>
            </div>
            <button onClick={() => setIsContributeModalOpen(true)} className="bg-white/5 border border-[#D4AF37]/30 text-[#D4AF37] px-6 py-3 rounded-sm text-sm hover:bg-[#D4AF37]/10 transition-colors shrink-0" data-id="element-6770" data-mp="2xg0lee0vqkcw">
              Contribute to a Zone
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6771" data-mp="20svwi8vx52mo">
            {ZONES.map(zone => <div key={zone.id} className={`bg-black/40 border ${zone.borderColor} rounded-2xl overflow-hidden group hover:shadow-[0_0_30px_rgba(212,175,55,0.1)] transition-all`} data-id="element-6772" data-mp="2eog7mi5eogjj">
                <div className={`h-32 bg-gradient-to-br ${zone.color} relative p-6 flex items-end`} data-id="element-6773" data-mp="3l7eg2qmi6s11">
                  <div className="absolute top-4 right-4 bg-black/50 backdrop-blur-md px-3 py-1 rounded-full border border-white/10 text-xs text-white/80" data-id="element-6774" data-mp="3tmc2ef9s0q49">
                    {zone.progress}% Restored
                  </div>
                  <zone.icon className="absolute top-4 left-4 w-6 h-6 text-white/30" data-id="element-6775" data-mp="3js298vd0fnsh" />
                  <h3 className="font-serif text-2xl text-white relative z-10" data-id="element-6776" data-mp="3op2ckyih6cfw">
                    {zone.name}
                  </h3>
                </div>

                <div className="p-6" data-id="element-6777" data-mp="l8d7lgyjjasc">
                  <p className="text-white/60 text-sm mb-6 h-10" data-id="element-6778" data-mp="l7aer3hnqy9s">
                    {zone.description}
                  </p>

                  <div className="mb-6" data-id="element-6779" data-mp="biaelgrf5wu0">
                    <div className="flex justify-between text-xs mb-2" data-id="element-6780" data-mp="bh8vcp092417">
                      <span className="text-white/40" data-id="element-6781" data-mp="z7cw8om1zpoi">Progress</span>
                      <span className="text-[#D4AF37]" data-id="element-6782" data-mp="z61supwisjxx">
                        {zone.cardsContributed} / {zone.requiredCards} Cards
                      </span>
                    </div>
                    <div className="h-1.5 bg-white/10 rounded-full overflow-hidden" data-id="element-6783" data-mp="3bg1vjrzr0kbd">
                      <div className="h-full bg-[#D4AF37] rounded-full transition-all duration-1000" style={{
                    width: `${zone.progress}%`
                  }} data-id="element-6784" data-mp="37ezwpc9qua9x" />
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-lg p-3 mb-6 border border-white/5" data-id="element-6785" data-mp="3v8896ubcdtxx">
                    <p className="text-[10px] text-white/40 uppercase tracking-wider mb-1" data-id="element-6786" data-mp="3q7uba53gg0dm">
                      Accepted Cards
                    </p>
                    <p className="text-sm text-white/80" data-id="element-6787" data-mp="3ghravju07k59">{zone.accepted}</p>
                  </div>

                  <button onClick={() => {
                setSelectedZone(zone.id);
                setIsContributeModalOpen(true);
              }} className="w-full py-3 border border-white/10 rounded-sm text-sm text-white hover:bg-white/5 hover:border-[#D4AF37]/50 transition-colors" data-id="element-6788" data-mp="hrr5k4vwklrx">
                    Restore This Zone
                  </button>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 5 & 6: School Restoration Mode & Misal */}
      <section id="schools" className="py-24 bg-[#0B0C10] relative border-t border-white/5 overflow-hidden" data-id="element-6789" data-mp="13asxv79p1wee">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[100px] pointer-events-none" data-id="element-6790" data-mp="188ktab9js0nc" />

        <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-6791" data-mp="1d2fitvbu39uc">
          <div className="text-center mb-16" data-id="element-6792" data-mp="1hye254ib21nw">
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4" data-id="element-6793" data-mp="1i19ywpif2kar">
              For Educators
            </p>
            <h2 className="font-serif text-3xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-6794" data-mp="1qzlyhh19gsfc">
              Schools Can Restore Punjab Together
            </h2>
            <p className="text-white/60 max-w-2xl mx-auto text-lg" data-id="element-6795" data-mp="3o0zwwux72jqq">
              Schools can adopt the Lahore Treasury, create classroom Misls,
              collect cards, restore zones, and unlock eligibility for a
              sponsored trip to the live immersive experience.
            </p>
          </div>

          <div className="grid lg:grid-cols-12 gap-8" data-id="element-6796" data-mp="3s08pvxkeudl8">
            {/* School Dashboard Preview */}
            <div className="lg:col-span-7 bg-[#13151C] border border-white/10 rounded-2xl p-8 shadow-2xl" data-id="element-6797" data-mp="any4gx8b60gv">
              <div className="flex items-start justify-between mb-8 pb-8 border-b border-white/5" data-id="element-6798" data-mp="5p4pt1hac3f4">
                <div data-id="element-6799" data-mp="jk73wbwacz30">
                  <div className="flex items-center gap-3 mb-2" data-id="element-6800" data-mp="fkevq0at0uy6">
                    <GraduationCap className="w-6 h-6 text-[#D4AF37]" data-id="element-6801" data-mp="fiu3ihbypo3h" />
                    <h3 className="font-serif text-2xl text-white" data-id="element-6802" data-mp="2leeu0up3cvtj">
                      Bramalea Secondary School
                    </h3>
                  </div>
                  <p className="text-white/40 text-sm" data-id="element-6803" data-mp="277usykz54vr9">Brampton, Ontario</p>
                </div>
                <div className="text-right" data-id="element-6804" data-mp="2bahkslvtagog">
                  <p className="text-3xl font-serif text-[#D4AF37]" data-id="element-6805" data-mp="2b96ikqi0lgzg">42%</p>
                  <p className="text-xs text-white/40 uppercase tracking-wider" data-id="element-6806" data-mp="1xhi5ajw14mgw">
                    School Progress
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-id="element-6807" data-mp="1njvo1qr9citr">
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6808" data-mp="1sjgqwc9jtbbr">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6809" data-mp="1sh3yt6a2tkkz">Classes Active</p>
                  <p className="text-xl text-white" data-id="element-6810" data-mp="1eljr76zssosv">3</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6811" data-mp="1jjtpvrkqe7j8">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6812" data-mp="38xgovhou2qwd">Misls Created</p>
                  <p className="text-xl text-white" data-id="element-6813" data-mp="3dwji84vgb8oi">5</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6814" data-mp="3dugu5qduzpmf">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6815" data-mp="qb2upds7v80w">Cards Given</p>
                  <p className="text-xl text-white" data-id="element-6816" data-mp="le2rdamr4jdh">128</p>
                </div>
                <div className="bg-black/30 p-4 rounded-lg border border-white/5" data-id="element-6817" data-mp="gl9n0nv73d1x">
                  <p className="text-white/40 text-xs mb-1" data-id="element-6818" data-mp="ckpsls20anjh">Top Zone</p>
                  <p className="text-sm text-[#D4AF37] truncate" data-id="element-6819" data-mp="cin1f4p4aod6">
                    Lahore Court
                  </p>
                </div>
              </div>

              <div className="space-y-4" data-id="element-6820" data-mp="2lieh2ng6ovb">
                <p className="text-sm font-medium text-white/80" data-id="element-6821" data-mp="2omls8w7m0q7">
                  Trip Eligibility Milestones
                </p>
                {[{
                label: 'School Treasury Badge',
                threshold: 25,
                achieved: true
              }, {
                label: 'Star of Punjab Challenge',
                threshold: 50,
                achieved: false
              }, {
                label: 'Treasury Night Invite',
                threshold: 75,
                achieved: false
              }, {
                label: 'Bus Sponsorship Priority',
                threshold: 100,
                achieved: false
              }].map((m, i) => <div key={i} className={`flex items-center gap-4 p-3 rounded-lg border ${m.achieved ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-black/20 border-white/5'}`} data-id="element-6822" data-mp="2mzoyaz34yulz" data-mpi={i}>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${m.achieved ? 'bg-[#D4AF37] text-black' : 'bg-white/10 text-white/30'}`} data-id="element-6823" data-mp="2w1epq4x0ptjr">
                      {m.achieved ? <Star className="w-3 h-3" data-id="element-6824" data-mp="g6w9ilaq8m94" /> : <span className="text-[10px]" data-id="element-6825" data-mp="hp6pcs7my9ig">{m.threshold}%</span>}
                    </div>
                    <span className={`text-sm ${m.achieved ? 'text-[#D4AF37]' : 'text-white/50'}`} data-id="element-6826" data-mp="mmornzr5dqhh">
                      {m.label}
                    </span>
                  </div>)}
              </div>
            </div>

            {/* Misal Cards */}
            <div className="lg:col-span-5 space-y-4" data-id="element-6827" data-mp="3uwgqzrnoo5ma">
              <h3 className="font-serif text-xl text-white mb-6" data-id="element-6828" data-mp="3om5dspzi7nd">
                Restore as a Misal
              </h3>
              {[{
              name: 'Sukerchakia Misl',
              rank: 'Court Class',
              score: 850,
              zone: 'Lahore Court'
            }, {
              name: 'Ahluwalia Misl',
              rank: 'Treasury Class',
              score: 620,
              zone: 'Frontier Defenders'
            }, {
              name: 'Ramgarhia Misl',
              rank: 'Darbar School',
              score: 410,
              zone: 'Sacred Punjab'
            }].map((misl, i) => <div key={i} className="bg-white/5 border border-white/10 rounded-xl p-5 flex items-center justify-between hover:bg-white/10 transition-colors cursor-pointer" data-id="element-6829" data-mp="2zsvb171f7qkp" data-mpi={i}>
                  <div className="flex items-center gap-4" data-id="element-6830" data-mp="2ptxk5xgivkyj">
                    <div className="w-12 h-12 rounded-full bg-black/50 border border-[#D4AF37]/30 flex items-center justify-center" data-id="element-6831" data-mp="2lt3t4v7s9q1f">
                      <Shield className="w-5 h-5 text-[#D4AF37]" data-id="element-6832" data-mp="2gxezt9mcx5dk" />
                    </div>
                    <div data-id="element-6833" data-mp="2bylkdbnif4ar">
                      <p className="text-white font-medium" data-id="element-6834" data-mp="f3yy9hoqe63t">{misl.name}</p>
                      <p className="text-xs text-[#D4AF37]" data-id="element-6835" data-mp="f1uy9atydr8t">{misl.rank}</p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-6836" data-mp="24lme0mqwghla">
                    <p className="text-white font-serif" data-id="element-6837" data-mp="24jrv1dz6uzju">{misl.score} pts</p>
                    <p className="text-[10px] text-white/40 uppercase" data-id="element-6838" data-mp="29d4ezasxai69">
                      {misl.zone}
                    </p>
                  </div>
                </div>)}

              <div className="pt-6 mt-6 border-t border-white/10 flex flex-col gap-3" data-id="element-6839" data-mp="1zlgl738w8p1m">
                <button className="w-full bg-[#D4AF37] text-black py-3 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors" data-id="element-6840" data-mp="2nbawhgdke5jr">
                  Register Your School
                </button>
                <button className="w-full bg-transparent border border-white/20 text-white py-3 rounded-sm text-sm hover:bg-white/5 transition-colors" data-id="element-6841" data-mp="2defwsu5k1z3w">
                  View School Leaderboard
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 7: Restoration Rewards */}
      <section className="py-24 bg-[#111218] relative border-t border-white/5" data-id="element-6842" data-mp="16yvt20klg8x0">
        <div className="max-w-7xl mx-auto px-6" data-id="element-6843" data-mp="3l426yv65cq3e">
          <div className="text-center mb-16" data-id="element-6844" data-mp="3g68whpxt99xb">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6845" data-mp="3g5h2uknrxm9o">Restoration Unlocks</h2>
            <p className="text-white/60 max-w-2xl mx-auto" data-id="element-6846" data-mp="3u5i3q041gtp5">
              As your school or misal restores the Punjab Memory Map, you unlock exclusive rewards, challenges, and live event access.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-6847" data-mp="32cho4kvtfw36">
            {[{
            title: 'School Treasury Badge',
            threshold: '25% Restoration',
            unlocked: true,
            icon: Shield
          }, {
            title: 'Star of Punjab Challenge',
            threshold: '50% Restoration',
            unlocked: false,
            icon: Star
          }, {
            title: 'Treasury Night Invite Eligibility',
            threshold: '75% Restoration',
            unlocked: false,
            icon: Ticket
          }, {
            title: 'Bus Sponsorship Priority',
            threshold: '100% Restoration',
            unlocked: false,
            icon: Bus
          }, {
            title: 'Live Night Edition Collectible',
            threshold: 'Live Attendance',
            unlocked: false,
            icon: Gem
          }, {
            title: 'Witness of the Restored Court',
            threshold: 'Special Badge',
            unlocked: false,
            icon: Crown
          }].map((reward, i) => <div key={i} className={`p-6 rounded-xl border ${reward.unlocked ? 'bg-[#D4AF37]/10 border-[#D4AF37]/30' : 'bg-black/40 border-white/10'} relative overflow-hidden`} data-id="element-6848" data-mp="36350k8uplypo" data-mpi={i}>
                <reward.icon className={`w-8 h-8 mb-4 ${reward.unlocked ? 'text-[#D4AF37]' : 'text-white/20'}`} data-id="element-6849" data-mp="3b4ua1k2aa4bn" />
                <h3 className={`font-serif text-xl mb-1 ${reward.unlocked ? 'text-white' : 'text-white/50'}`} data-id="element-6850" data-mp="3k3fqn9o7399s">{reward.title}</h3>
                <p className={`text-sm ${reward.unlocked ? 'text-[#D4AF37]' : 'text-white/30'}`} data-id="element-6851" data-mp="3p17tuzuaihta">{reward.threshold}</p>
                {!reward.unlocked && <div className="absolute top-4 right-4 text-[10px] uppercase tracking-widest text-white/20 border border-white/10 px-2 py-1 rounded" data-id="element-6852" data-mp="1pr9gme71eyj2">Locked</div>}
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 8: Live Experience Connection */}
      <section className="py-24 bg-[#8B0000]/10 relative border-t border-[#8B0000]/20" data-id="element-6853" data-mp="1icpejwcdmmu5">
        <div className="max-w-5xl mx-auto px-6 text-center" data-id="element-6854" data-mp="1dffdfjfmkmqh">
          <Ticket className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" data-id="element-6855" data-mp="18gciwsx9tywr" />
          <h2 className="font-serif text-3xl sm:text-5xl text-[#FDFBF7] mb-6" data-id="element-6856" data-mp="18dzwa2gkj0dd">
            From Digital Restoration to the Live Court
          </h2>
          <p className="text-white/70 text-lg max-w-3xl mx-auto mb-12 leading-relaxed" data-id="element-6857" data-mp="215jf3l996ceu">
            When a school restores enough of the Punjab Memory Map, it unlocks
            eligibility for a subsidized or sponsor-funded visit to The Kingdoms
            of Punjab live immersive experience.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-12" data-id="element-6858" data-mp="krtc6htvdu6s">
            {[{
            step: '1',
            text: 'Restore zones online'
          }, {
            step: '2',
            text: 'Reach trip eligibility'
          }, {
            step: '3',
            text: 'Sponsor funds bus'
          }, {
            step: '4',
            text: 'Attend Live Night'
          }].map((s, i) => <div key={i} className="bg-black/40 border border-[#D4AF37]/20 rounded-xl p-6 relative" data-id="element-6859" data-mp="2k94tvm27hovc" data-mpi={i}>
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-bold text-sm" data-id="element-6860" data-mp="2p0mug90bhnfs">
                  {s.step}
                </div>
                <p className="text-white/90 text-sm mt-2" data-id="element-6861" data-mp="2g8b3mm1ajxza">{s.text}</p>
              </div>)}
          </div>

          <button className="bg-[#D4AF37] text-black px-8 py-4 rounded-sm text-sm font-medium hover:bg-[#FDFBF7] transition-colors inline-flex items-center gap-2" data-id="element-6862" data-mp="21b0uvkg19679">
            <HeartHandshake className="w-4 h-4" data-id="element-6863" data-mp="1moldyl4hxxwj" />
            Sponsor a School Bus
          </button>
        </div>
      </section>

      {/* SECTION 9: Leaderboards */}
      <section className="py-24 bg-[#0B0C10] relative border-t border-white/5" data-id="element-6864" data-mp="1wlge18gf46cx">
        <div className="max-w-6xl mx-auto px-6" data-id="element-6865" data-mp="1wjdenxtdl0vj">
          <div className="text-center mb-16" data-id="element-6866" data-mp="1rlljmuxfoyn4">
            <h2 className="font-serif text-3xl sm:text-4xl text-[#FDFBF7] mb-4" data-id="element-6867" data-mp="1rozny8pnrhx7">
              Restoration Leaderboards
            </h2>
            <div className="flex justify-center gap-4 mt-8" data-id="element-6868" data-mp="21lm9z0tq0f0t">
              {['Schools', 'Classes / Misls', 'Monthly Challenge', 'Zones Restored'].map((tab, i) => <button key={i} className={`px-4 py-2 text-sm rounded-full border ${i === 0 ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]' : 'bg-transparent border-white/10 text-white/50 hover:text-white'}`} data-id="element-6869" data-mp="2af7ocj1z3u9p" data-mpi={i}>
                  {tab}
                </button>)}
            </div>
          </div>

          <div className="bg-[#111218] border border-white/10 rounded-2xl overflow-hidden" data-id="element-6870" data-mp="19x1impz4ejdm">
            <div className="grid grid-cols-12 gap-4 p-4 border-b border-white/10 text-xs text-white/40 uppercase tracking-wider bg-black/20" data-id="element-6871" data-mp="1ey7bcnss66n5">
              <div className="col-span-1 text-center" data-id="element-6872" data-mp="1ju63mmh8c8su">Rank</div>
              <div className="col-span-5" data-id="element-6873" data-mp="m9tbs3m1n29h">School / Misal</div>
              <div className="col-span-2 text-center" data-id="element-6874" data-mp="r7bn20nj9tkn">Restoration %</div>
              <div className="col-span-2 text-center" data-id="element-6875" data-mp="r5h2wok365hm">Cards</div>
              <div className="col-span-2 text-right pr-4" data-id="element-6876" data-mp="w5wcr655suuu">Trip Status</div>
            </div>
            {[{
            rank: 1,
            name: 'Khalsa Community School',
            city: 'Brampton',
            progress: 92,
            cards: 450,
            status: 'Eligible'
          }, {
            rank: 2,
            name: 'Bramalea Secondary',
            city: 'Brampton',
            progress: 42,
            cards: 128,
            status: 'In Progress'
          }, {
            rank: 3,
            name: 'David Suzuki SS',
            city: 'Brampton',
            progress: 28,
            cards: 85,
            status: 'In Progress'
          }, {
            rank: 4,
            name: 'Lincoln M. Alexander',
            city: 'Mississauga',
            progress: 15,
            cards: 42,
            status: 'Started'
          }].map((row, i) => <div key={i} className="grid grid-cols-12 gap-4 p-4 border-b border-white/5 items-center hover:bg-white/5 transition-colors" data-id="element-6877" data-mp="1p1mcn2q1cj4h" data-mpi={i}>
                <div className="col-span-1 text-center" data-id="element-6878" data-mp="1f36n346nphx3">
                  {i === 0 ? <Trophy className="w-5 h-5 text-[#D4AF37] mx-auto" data-id="element-6879" data-mp="oylat4hssemx" /> : <span className="text-white/50" data-id="element-6880" data-mp="jzifwcvj7oso">{row.rank}</span>}
                </div>
                <div className="col-span-5" data-id="element-6881" data-mp="xz1b6538v5nk">
                  <p className="text-white font-medium" data-id="element-6882" data-mp="xyrksgeaewhc">{row.name}</p>
                  <p className="text-white/40 text-xs" data-id="element-6883" data-mp="szyhthwmmnvf">{row.city}</p>
                </div>
                <div className="col-span-2 text-center" data-id="element-6884" data-mp="5a45r2k4t3c8">
                  <span className="text-[#D4AF37] font-serif text-lg" data-id="element-6885" data-mp="58jcrhdgtsgh">
                    {row.progress}%
                  </span>
                </div>
                <div className="col-span-2 text-center text-white/70" data-id="element-6886" data-mp="f0p02dsrvlpc">
                  {row.cards}
                </div>
                <div className="col-span-2 text-right pr-4" data-id="element-6887" data-mp="a85p7olgz4o8">
                  <span className={`text-xs px-2 py-1 rounded-full ${row.status === 'Eligible' ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`} data-id="element-6888" data-mp="3ip0gs7wth2n0">
                    {row.status}
                  </span>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* SECTION 4: Contribute Modal */}
      <AnimatePresence data-id="element-6889" data-mp="1k5lww2frasds">
        {isContributeModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 sm:p-6" data-id="element-6890" data-mp="1p7urt4c6w4ij">
            <motion.div initial={{
          scale: 0.95,
          y: 20
        }} animate={{
          scale: 1,
          y: 0
        }} exit={{
          scale: 0.95,
          y: 20
        }} className="bg-[#111218] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden shadow-2xl" data-id="element-6891" data-mp="230arpw9snmf8">
              <div className="p-6 border-b border-white/10 flex items-center justify-between bg-black/20" data-id="element-6892" data-mp="8fhavgto35xq">
                <div data-id="element-6893" data-mp="3melzlzu4knv">
                  <h3 className="font-serif text-2xl text-white" data-id="element-6894" data-mp="3llcwedelj2k">
                    Choose a Card to Restore Punjab
                  </h3>
                  <p className="text-white/50 text-sm mt-1" data-id="element-6895" data-mp="3pvfkr1wcxf9y">
                    {selectedZone ? `Contributing to ${ZONES.find(z => z.id === selectedZone)?.name}` : 'Select a card from your collection to contribute.'}
                  </p>
                </div>
                <button onClick={() => setIsContributeModalOpen(false)} className="text-white/50 hover:text-white" data-id="element-6896" data-mp="3bvwop7gtlue0">
                  <X className="w-6 h-6" data-id="element-6897" data-mp="1a3s0o72fzh8k" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto flex-1 bg-[#0B0C10]" data-id="element-6898" data-mp="1a1xeyqjcjl3f">
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-lg p-4 mb-6 flex items-start gap-3" data-id="element-6899" data-mp="1hmri476ceypa">
                  <Sparkles className="w-5 h-5 text-[#D4AF37] shrink-0 mt-0.5" data-id="element-6900" data-mp="1mkavz5z4p9gp" />
                  <p className="text-sm text-[#D4AF37]/90" data-id="element-6901" data-mp="1rdm3l9brlu9o">
                    <strong data-id="element-6902" data-mp="1wcgn6dhk3muu">Important:</strong> Contributing a card does not
                    remove it from your collection. It adds its story power to
                    the Punjab Memory Map.
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4" data-id="element-6903" data-mp="13nrvt66ayvwa">
                  {USER_CARDS.map(card => <div key={card.id} className="bg-[#13151C] border border-white/10 rounded-xl overflow-hidden group relative" data-id="element-6904" data-mp="1co7snwsjj4s6">
                      {animatingCard === card.id && <motion.div initial={{
                  opacity: 0
                }} animate={{
                  opacity: 1
                }} className="absolute inset-0 bg-[#D4AF37]/20 z-20 flex items-center justify-center backdrop-blur-sm" data-id="element-6905" data-mp="2o1s4pm9ha4j7">
                          <Sparkles className="w-8 h-8 text-[#D4AF37] animate-pulse" data-id="element-6906" data-mp="2rjpnp7raddz0" />
                        </motion.div>}
                      <div className="aspect-[3/4] relative overflow-hidden bg-black" data-id="element-6907" data-mp="30m8n1hvv58gi">
                        <img src={card.img} alt={card.name} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition-opacity" data-id="element-6908" data-mp="3favdofqxxmn1" />
                        <div className="absolute top-2 right-2 bg-black/60 backdrop-blur-md px-2 py-1 rounded text-[10px] text-[#D4AF37] border border-[#D4AF37]/30 uppercase tracking-wider" data-id="element-6909" data-mp="3occnjgdr8s3a">
                          +{card.value} Pts
                        </div>
                      </div>
                      <div className="p-4" data-id="element-6910" data-mp="bqnu0wiaa1az">
                        <p className="text-white font-serif truncate" data-id="element-6911" data-mp="bpl3ipovi0vl">
                          {card.name}
                        </p>
                        <p className="text-white/40 text-xs mb-4" data-id="element-6912" data-mp="2qhivha25dez">
                          {card.rarity}
                        </p>
                        <button onClick={() => handleContribute(card.id)} disabled={animatingCard !== null} className="w-full bg-white/5 border border-white/10 text-white py-2 rounded text-xs hover:bg-[#D4AF37] hover:text-black hover:border-[#D4AF37] transition-colors" data-id="element-6913" data-mp="3k6fw9cbyi4vy">
                          Contribute to Restoration
                        </button>
                      </div>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}